import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/reveal";
import SEO from "@/components/seo";
import { Printer, Phone, CheckCircle2, ArrowLeft } from "lucide-react";

const BOOK_A_CALL = "https://calendly.com/carla-tallylassie";

export interface GuideSection {
  heading: string;
  body?: string;
  checklist?: string[];
}

interface GuidePageProps {
  eyebrow: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  path: string;
  heroImg: string;
  intro: string;
  sections: GuideSection[];
  ctaHeading?: string;
  ctaBody?: string;
}

export default function GuidePage({
  eyebrow,
  title,
  metaTitle,
  metaDescription,
  path,
  heroImg,
  intro,
  sections,
  ctaHeading = "Going out of town or back to a busy schedule?",
  ctaBody = "Let's make a plan for your pet. Book a quick, no-pressure call with Carla and we'll match you with the right care.",
}: GuidePageProps) {
  return (
    <div className="flex flex-col w-full">
      <SEO title={metaTitle} description={metaDescription} path={path} />

      <section className="relative bg-primary text-primary-foreground py-20 md:py-28 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-[0.14] mix-blend-overlay"
          style={{ backgroundImage: `url('${heroImg}')` }}
        ></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10 max-w-3xl text-center">
          <Reveal>
            <p className="uppercase tracking-[0.2em] text-sm text-primary-foreground/70 mb-4 font-medium">
              {eyebrow}
            </p>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              {title}
            </h1>
          </Reveal>
        </div>
      </section>

      <article className="py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <Reveal>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-10 print:hidden">
              <Button asChild variant="ghost" className="text-primary hover:text-primary/80 -ml-3">
                <Link href="/resources">
                  <ArrowLeft size={16} className="mr-2" /> All Resources
                </Link>
              </Button>
              <button
                onClick={() => typeof window !== "undefined" && window.print()}
                className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                <Printer size={16} /> Save or print this guide
              </button>
            </div>
          </Reveal>

          <Reveal>
            <p className="text-xl text-foreground/80 leading-relaxed mb-12 font-serif">{intro}</p>
          </Reveal>

          <div className="space-y-12">
            {sections.map((section, i) => (
              <Reveal key={i} delay={i * 40}>
                <section>
                  <h2 className="font-serif text-2xl md:text-3xl font-bold text-foreground mb-4">
                    {section.heading}
                  </h2>
                  {section.body && (
                    <p className="text-foreground/80 leading-relaxed text-lg">{section.body}</p>
                  )}
                  {section.checklist && (
                    <ul className="mt-4 space-y-3">
                      {section.checklist.map((item, j) => (
                        <li key={j} className="flex items-start gap-3 text-foreground/80 text-lg">
                          <CheckCircle2 size={20} className="text-primary mt-1 shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </Reveal>
            ))}
          </div>
        </div>
      </article>

      <section className="pb-20 print:hidden">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <Reveal>
            <div
              className="bg-primary text-white p-10 md:p-12 text-center relative overflow-hidden"
              style={{ borderRadius: "32px 0 32px 0" }}
            >
              <h2 className="font-serif text-2xl md:text-3xl font-bold mb-4">{ctaHeading}</h2>
              <p className="text-lg text-white/90 max-w-xl mx-auto mb-8">{ctaBody}</p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90 h-14 px-8 text-lg w-full sm:w-auto">
                  <a href={BOOK_A_CALL} target="_blank" rel="noopener noreferrer">
                    <Phone size={18} className="mr-2" /> Book a Free Call
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 h-14 px-8 text-lg w-full sm:w-auto"
                >
                  <Link href="/care-plan-finder">
                    Find Your Care Plan
                  </Link>
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
